import { AtSign, FolderInput, ListOrdered, PenLine } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { useWorkflowsOptional } from "../workflows/context/WorkflowsContext";

export type MoveTarget = { id: string | null; label: string };

const Action: FC<{
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
  testId: string;
}> = ({ label, onClick, disabled, children, testId }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top">{label}</TooltipContent>
  </Tooltip>
);

type Props = {
  ids: string[];
  moveTargets: MoveTarget[];
  onMove: (target: string | null) => void;
  onAsk: () => void;
};

/**
 * The selection bar's quick actions: retitle or number just these files
 * through the case's workflows, move them, or hand them to Casey.
 */
export const SelectionActions: FC<Props> = ({
  ids,
  moveTargets,
  onMove,
  onAsk
}) => {
  const w = useWorkflowsOptional();
  const scope = { kind: "selection", nodeIds: ids } as const;
  const busy = Boolean(w?.running);
  return (
    <span className="flex items-center gap-0.5">
      {w ? (
        <>
          <Action
            label={`Retitle ${ids.length} Files With Casey`}
            onClick={() => w.run("retitle", scope)}
            disabled={busy}
            testId="selection-retitle"
          >
            <PenLine className="size-3.5" />
          </Action>
          <Action
            label={`Number ${ids.length} Files With Casey`}
            onClick={() => w.run("number", scope)}
            disabled={busy}
            testId="selection-number"
          >
            <ListOrdered className="size-3.5" />
          </Action>
        </>
      ) : null}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label="Move To"
                disabled={moveTargets.length === 0}
                data-testid="selection-move"
              >
                <FolderInput className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">Move To</TooltipContent>
        </Tooltip>
        <DropdownMenuContent
          align="end"
          side="top"
          className="max-h-72 w-60 overflow-y-auto"
        >
          <DropdownMenuLabel>Move {ids.length} Files To</DropdownMenuLabel>
          {moveTargets.map((t) => (
            <DropdownMenuItem
              key={t.id ?? "root"}
              onSelect={() => onMove(t.id)}
            >
              <span className="truncate">{t.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Action
        label="Ask Casey About These"
        onClick={onAsk}
        testId="selection-ask"
      >
        <AtSign className="size-3.5" />
      </Action>
    </span>
  );
};
